import * as React from 'react';
import { Image } from '@/hen-ui/atomics/Image';
import { Text } from '@/hen-ui/atomics/Text';
import { Link } from '@/hen-ui/atomics/Link';
import { Separator } from '@/hen-ui/atomics/Separator';
import { Card } from '@/hen-ui/composites/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import {
  articleCardContainerVariants,
  articleCardImageVariants,
} from './article-card.variants';
import { ArticleCardOverlay } from './ArticleCardOverlay';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { cn } from '@/hen-ui/utils';

export interface ArticleCardProps {
  /** Featured image URL - omit to render the card without an image */
  image?: string;
  /** When false, the image is not rendered even if `image` is set (stacked and overlay). Default: true */
  showImage?: boolean;
  /** Alt text for the image */
  imageAlt?: string;
  /** Article title heading - required */
  title: string;
  /** Purple eyebrow/category text above title - optional */
  brow?: string;
  /** Alias for `brow`; takes priority when both are provided */
  browLabel?: string;
  /** Author name (prefixed with "By") - optional */
  author?: string;
  /** Publication date string - optional */
  date?: string;
  /**
   * Plain-text excerpt when `variant` is `"excerpt"` (stacked only).
   * Common on `ArticleRow` rails. Ignored for `layout="overlay"`.
   * When `variant` is `"excerpt"` but this is missing or whitespace-only, author/date still
   * render as the byline fallback (same row as `variant="byline"`).
   */
  excerpt?: string;
  /**
   * Below-title presentation (stacked only). `byline` — author + date when either is set.
   * `excerpt` — excerpt text when `excerpt` is non-empty after trim; otherwise falls back to
   * the byline when `author` or `date` is set, or renders nothing below the title.
   * Overlay always behaves as `byline` regardless of this prop. More values may be added later.
   */
  variant?: 'byline' | 'excerpt';
  /** Link URL for the card - optional. A blank or `#` URL renders no link. */
  href?: string;
  /** Size variant - default: "lg"  (ignored when layout is `overlay`)*/
  size?: 'lg' | 'md' | 'sm' | 'compact' | 'xs';
  /** Border variant - default: "none" (ignored when layout is `overlay`) */
  border?: 'none' | 'padding' | 'stroke' | 'shadow';
  /** Adds `shadow-md` on hover (ignored when `layout="overlay"`). */
  hoverStyle?: 'none' | 'shadow';
  /**
   * How image and copy are arranged. Same props and data as `stacked`; only presentation changes.
   * `stacked` — image above text in a Card (default).
   * `overlay` — full-bleed image, bottom gradient, left-aligned white type (Figma Article Row tile).
   */
  layout?: 'stacked' | 'overlay';
}

/**
 * ArticleCard component following HEN Design System standards.
 *
 * A composite component that combines Image, Heading, and Text atomics to display
 * article previews with featured image, brow text, title, author, and date.
 * Standardizes article preview displays across HEN applications.
 *
 * @example
 * // Large card with all content
 * <ArticleCard
 *   image="/images/article.jpg"
 *   imageAlt="Article preview"
 *   brow="Technology"
 *   title="The Future of Online Learning"
 *   author="Jane Doe"
 *   date="September 25, 2025"
 *   href="/articles/future-of-learning"
 *   size="lg"
 * />
 *
 * @example
 * // Compact horizontal layout
 * <ArticleCard
 *   image="/images/article-thumb.jpg"
 *   imageAlt="Article thumbnail"
 *   title="Quick Read Article Title"
 *   author="John Smith"
 *   date="October 1, 2025"
 *   href="/articles/quick-read"
 *   size="compact"
 * />
 */
export const ArticleCard = React.forwardRef<HTMLDivElement, ArticleCardProps>(
  (
    {
      image,
      showImage = true,
      imageAlt,
      title,
      brow,
      browLabel,
      author,
      date,
      excerpt,
      variant = 'byline',
      href,
      size = 'lg',
      border = 'none',
      hoverStyle = 'none',
      layout = 'stacked',
    },
    ref,
  ) => {
    const resolvedImage = showImage ? image : undefined;
    const resolvedBrow = browLabel ?? brow;
    const trimmedHref = href?.trim();
    const linkHref = trimmedHref && trimmedHref !== '#' ? href : undefined;

    if (layout === 'overlay') {
      return (
        <ArticleCardOverlay
          ref={ref}
          image={resolvedImage}
          imageAlt={imageAlt}
          title={title}
          brow={brow}
          browLabel={browLabel}
          author={author}
          date={date}
          href={linkHref}
        />
      );
    }

    const headingSize = {
      lg: 'h3' as const,
      md: 'h3' as const,
      sm: 'h3' as const,
      compact: 'h3' as const,
      xs: 'h3' as const,
    }[size];

    // Stacked card: flex direction of image + body (compact and xs = horizontal thumb row)
    const isCompact = size === 'compact' || size === 'xs';
    // Determine the content flow (vertical or horizontal) based on the size variant
    const contentFlow = isCompact ? 'horizontal' : 'vertical';
    const containerGap = {
      lg: 'md' as const,
      md: 'md' as const,
      sm: 'sm' as const,
      compact: 'sm' as const,
      xs: 'sm' as const,
    }[size];

    // Set card variant based on border - transparent when no border, white otherwise
    const cardVariant =
      border === 'none' ? 'background-transparent' : 'background-white';

    const isExtraSmall = size === 'xs';
    const titleClassName =
      variant === 'excerpt' || isExtraSmall ? 'line-clamp-3' : 'line-clamp-2';

    const textClampClassName = variant === 'excerpt' ? 'line-clamp-2' : '';
    const cardBody = (
      <Flex direction='column' gap={isExtraSmall ? 'none' : 'xxs'}>
        {isExtraSmall ? (
          <>
            {resolvedBrow && <Text variant='l1'>{resolvedBrow}</Text>}
            <Text asChild variant='b5'>
              <h3 className={titleClassName}>{title}</h3>
            </Text>
          </>
        ) : (
          <SectionHeader
            size={headingSize}
            title={title}
            brow={resolvedBrow}
            align='left'
            titleClassName={titleClassName}
          />
        )}
        {variant === 'excerpt' && excerpt?.trim() ? (
          <Text variant='b4' color='subtext' className={textClampClassName}>
            {excerpt.trim()}
          </Text>
        ) : author || date ? (
          <Flex direction='row' gap='xxs' align='center' wrap='wrap'>
            {author && (
              <Text variant='l1' color='subtext'>
                By {author}
              </Text>
            )}
            {author && date && (
              <Separator
                orientation='vertical'
                className='h-4 bg-neutral-100'
              />
            )}
            {date && (
              <Text variant='l1' color='subtext'>
                {date}
              </Text>
            )}
          </Flex>
        ) : null}
      </Flex>
    );

    // No image: Card root has no outer padding when border is `none`; compact text-only
    // tiles need inner padding (Figma padding-lg). Bordered cards already use `p-lg` on Card.
    const cardContent = resolvedImage ? (
      <>
        <div
          className={cn(
            'overflow-hidden',
            isExtraSmall ? 'shrink-0 rounded-md' : 'rounded-lg',
          )}
        >
          <Image
            src={resolvedImage}
            alt={imageAlt ?? title}
            loading='lazy'
            decoding='async'
            className={articleCardImageVariants({ size, border })}
          />
        </div>
        <Card.Content
          padding={
            border === 'none'
              ? 'p-0'
              : isCompact
                ? 'px-md pt-0 pb-sm'
                : 'px-md py-sm'
          }
          className='gap-xxs'
        >
          {cardBody}
        </Card.Content>
      </>
    ) : (
      <Flex direction='column' gap='xxs' className='w-full'>
        <Card.Content
          padding={
            isCompact && border === 'none' ? 'p-md md:px-lg md:py-lg' : 'p-0'
          }
          className={isCompact && border === 'none' ? 'gap-xxs' : undefined}
        >
          {cardBody}
        </Card.Content>
      </Flex>
    );

    // Wrap in link if href is provided
    if (linkHref) {
      return (
        <Flex ref={ref} className='group'>
          <Link
            href={linkHref}
            underline='none'
            className={cn(
              'block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-4 rounded-xl',
              !isExtraSmall &&
                '[&_h1]:transition-colors [&_h2]:transition-colors [&_h3]:transition-colors [&_h4]:transition-colors [&_h5]:transition-colors [&_h6]:transition-colors md:[&_h1]:group-hover:text-primary-700 md:[&_h2]:group-hover:text-primary-700 md:[&_h3]:group-hover:text-primary-700 md:[&_h4]:group-hover:text-primary-700 md:[&_h5]:group-hover:text-primary-700 md:[&_h6]:group-hover:text-primary-700',
            )}
            aria-label={`Link to: ${title}`}
          >
            <Card
              variant={cardVariant}
              border={border}
              data-hen-id={COMPONENT_IDS.ArticleCard}
              className={articleCardContainerVariants({
                layout: contentFlow,
                gap: containerGap,
                hoverStyle: hoverStyle,
              })}
            >
              {cardContent}
            </Card>
          </Link>
        </Flex>
      );
    }

    // Otherwise, render as a Card
    return (
      <Card
        ref={ref}
        role='article'
        aria-label={title}
        variant={cardVariant}
        border={border}
        data-hen-id={COMPONENT_IDS.ArticleCard}
        className={articleCardContainerVariants({
          layout: contentFlow,
          gap: containerGap,
          hoverStyle: hoverStyle,
        })}
      >
        {cardContent}
      </Card>
    );
  },
);

ArticleCard.displayName = 'ArticleCard';
